import { stateWord } from "@lafayette/domain";
import type { RequestConflict } from "@lafayette/schemas";

import { describeStaffFailure, neutralForbidden } from "../failure-words";
import { conflictCodeLabels } from "./labels";

// How the Requests pages tell a staff member what went wrong. Rules:
//   1. No note, reason, customer text or id goes into a message. The API sends none either: a refusal holds a code and, for a
//      conflict, the current version and state.
//   2. A refusal for access (403) and a missing id (404) read the same. The page never says whether a request exists or that the staff
//      member "is not assigned".
//   3. A conflict says in plain words that the request changed, what state it is in now when the server said so, and that the page
//      reloaded. Nothing was changed by the refused command.

export type RequestFailureKind =
  | "conflict"
  | "invalid"
  | "bad_request"
  | "forbidden"
  | "unauthenticated"
  | "rate_limited"
  | "unavailable"
  | "network"
  | "response"
  | "unknown";

export type RequestFailure = Readonly<{
  kind: RequestFailureKind;
  message: string;
  /** The same command may be sent again with the SAME idempotency key: it may or may not have run. */
  retryable: boolean;
  /** The page must read the request again: what the user saw is out of date. */
  reload: boolean;
  /** For a conflict: the code of the refusal. */
  conflictCode: RequestConflict["code"] | null;
  /** For a conflict: true for a stale version. */
  stale: boolean;
  requestId: string | null;
}>;

const NEUTRAL_FORBIDDEN_MESSAGE = neutralForbidden("this request");

function conflictMessage(conflict: RequestConflict | null): string {
  if (conflict === null) {
    return "The request changed, or the same action is still running. Nothing was changed by this attempt. The page shows the latest state.";
  }
  const now =
    conflict.currentState === null
      ? "You may not be able to open it any more."
      : `It is now ${stateWord("request", "staffShort", conflict.currentState).toLowerCase()}.`;
  return `${conflictCodeLabels[conflict.code]} ${now} Nothing was changed by this attempt. The page now shows the latest state. Check it, then act again if it is still needed.`;
}

export function describeRequestError(error: unknown): RequestFailure {
  const { api, ...shared } = describeStaffFailure(error, {
    forbidden: NEUTRAL_FORBIDDEN_MESSAGE,
    conflict: ({ detail }) => conflictMessage(detail.requestConflict),
    invalid: "The server did not accept this. Check the fields and try again. Nothing was changed.",
    // CHT-754: the browser could not send the bytes to the signed target. The message never holds the URL (it is a signed credential).
    upload:
      "The file could not be sent to storage. Check the connection and try again. Nothing was attached.",
  });
  const conflict = shared.kind === "conflict";
  return {
    ...shared,
    conflictCode: conflict ? (api.detail.requestConflict?.code ?? null) : null,
    stale: conflict && api.staleView,
  };
}
